import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { n2, shortDate } from "@/lib/format";
import { BandTuning } from "@/components/accuracy/BandTuning";

const avg = (arr) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0);

// Accuracy history of the Rotation Model's projected final score vs the real result,
// with a single-day + team filter and a side-by-side Model vs DraftKings comparison.
export function ScoreModelAccuracy() {
  const [data, setData] = useState(null);
  const [day, setDay] = useState("all");
  const [team, setTeam] = useState("all");

  useEffect(() => {
    api.get("/rotation/score-accuracy", { params: { limit: 400 } }).then((r) => setData(r.data)).catch(() => setData(false));
  }, []);

  const days = useMemo(() => (data && data.games ? [...new Set(data.games.map((g) => g.game_day))] : []), [data]);
  const teams = useMemo(() => (data && data.games ? [...new Set(data.games.flatMap((g) => [g.away_code, g.home_code]))].sort() : []), [data]);

  const games = useMemo(() => {
    if (!data || !data.games) return [];
    return data.games.filter((g) => (day === "all" || g.game_day === day) && (team === "all" || g.away_code === team || g.home_code === team));
  }, [data, day, team]);

  const summary = useMemo(() => {
    if (!games.length) return null;
    return {
      n: games.length,
      mae_spread: avg(games.map((g) => Math.abs(g.err_spread))),
      mae_total: avg(games.map((g) => Math.abs(g.err_total))),
      bias_spread: avg(games.map((g) => g.err_spread)),
      bias_total: avg(games.map((g) => g.err_total)),
      winner_pct: avg(games.map((g) => (g.winner_correct ? 100 : 0))),
    };
  }, [games]);

  const compare = useMemo(() => {
    const spread = games.filter((g) => g.market_line && g.market_line.spread_home != null);
    const total = games.filter((g) => g.market_line && g.market_line.total != null);
    const mk = (rows, modelErr, marketVal, actualKey) => rows.length ? {
      n: rows.length,
      model_mae: avg(rows.map((g) => Math.abs(modelErr(g)))),
      market_mae: avg(rows.map((g) => Math.abs(marketVal(g) - g.actual_line[actualKey]))),
      model_better: rows.filter((g) => Math.abs(modelErr(g)) < Math.abs(marketVal(g) - g.actual_line[actualKey])).length,
    } : null;
    return {
      spread: mk(spread, (g) => g.err_spread, (g) => g.market_line.spread_home, "spread_home"),
      total: mk(total, (g) => g.err_total, (g) => g.market_line.total, "total"),
    };
  }, [games]);

  if (data === null) return <p className="aa-empty" data-testid="score-acc-loading">Υπολογισμός…</p>;
  if (data === false) return null;

  const hasGraded = data.games && data.games.length > 0;
  return (
    <section className="aa-section" data-testid="score-model-accuracy">
      <h2>Rotation Model — προβλεπόμενο σκορ vs τελικό</h2>
      <p className="aa-explainer">
        Αποθηκευμένη πρόβλεψη σκορ πριν το tip-off, βαθμολογημένη στο πραγματικό τελικό. Μέσο λάθος σε spread/total για να βελτιώνονται οι συντελεστές.
      </p>
      {!hasGraded ? (
        <p className="aa-note" data-testid="score-acc-empty">
          Δεν υπάρχουν ακόμη ολοκληρωμένοι αγώνες με αποθηκευμένη πρόβλεψη. Εμφανίζονται την επόμενη μέρα μετά το ημερήσιο update ή όταν ανοίξεις τον αγώνα στη σελίδα Rotations.
        </p>
      ) : (
        <>
          <div className="aa-tabs" style={{ marginBottom: 14, gap: 10 }} data-testid="score-acc-filters">
            <select value={day} onChange={(e) => setDay(e.target.value)} data-testid="score-acc-day-filter"
                    style={{ background: "#0a1322", color: "#e6edf6", border: "1px solid #1a2a44", borderRadius: 8, padding: "7px 10px", fontSize: 13 }}>
              <option value="all">Όλες οι μέρες</option>
              {days.map((d) => <option key={d} value={d}>{shortDate(d)}</option>)}
            </select>
            <select value={team} onChange={(e) => setTeam(e.target.value)} data-testid="score-acc-team-filter"
                    style={{ background: "#0a1322", color: "#e6edf6", border: "1px solid #1a2a44", borderRadius: 8, padding: "7px 10px", fontSize: 13 }}>
              <option value="all">Όλες οι ομάδες</option>
              {teams.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          {!summary ? <p className="aa-note" data-testid="score-acc-nofilter">Κανένας αγώνας για το φίλτρο.</p> : (
            <>
              <div className="aa-cards">
                <div className="aa-card" data-testid="score-acc-mae-spread"><span>MAE Spread</span><strong>{n2(summary.mae_spread)}</strong><small>bias {summary.bias_spread > 0 ? "+" : ""}{n2(summary.bias_spread)}</small></div>
                <div className="aa-card" data-testid="score-acc-mae-total"><span>MAE Total</span><strong>{n2(summary.mae_total)}</strong><small>bias {summary.bias_total > 0 ? "+" : ""}{n2(summary.bias_total)}</small></div>
                <div className="aa-card" data-testid="score-acc-winner"><span>Σωστός νικητής</span><strong>{n2(summary.winner_pct)}%</strong><small>n={summary.n} αγώνες</small></div>
              </div>

              <h3 style={{ fontSize: 14, color: "#60a5fa", margin: "18px 0 8px" }}>Μοντέλο vs DraftKings</h3>
              <div className="aa-cards" data-testid="score-acc-market-compare">
                {["spread", "total"].map((k) => {
                  const c = compare[k];
                  if (!c) return <div key={k} className="aa-card"><span>{k === "spread" ? "Spread" : "Total"}</span><strong>—</strong><small>χωρίς γραμμή αγοράς</small></div>;
                  const better = c.model_mae < c.market_mae;
                  return (
                    <div key={k} className="aa-card" data-testid={`score-acc-cmp-${k}`}>
                      <span>{k === "spread" ? "Spread" : "Total"} MAE</span>
                      <strong>{n2(c.model_mae)}</strong>
                      <small>DraftKings {n2(c.market_mae)}</small>
                      <em className={better ? "positive" : "negative"}>{better ? "▲ καλύτερο" : "▼ χειρότερο"} · {c.model_better}/{c.n} αγ.</em>
                    </div>
                  );
                })}
              </div>

              <div className="aa-table-scroll">
                <table className="aa-table" data-testid="score-acc-table">
                  <thead><tr><th>Ημ/νία</th><th>Αγώνας</th><th className="r">Μοντέλο</th><th className="r">Τελικό</th><th className="r">Λάθος spread</th><th className="r">Λάθος total</th><th className="r">Νικητής</th></tr></thead>
                  <tbody>
                    {games.map((g) => (
                      <tr key={g.game_id}>
                        <td className="aa-date">{shortDate(g.game_day)}</td>
                        <td>{g.away_code} @ {g.home_code}</td>
                        <td className="r aa-number">{g.proj[g.away_code]}–{g.proj[g.home_code]}</td>
                        <td className="r aa-number">{g.actual[g.away_code]}–{g.actual[g.home_code]}</td>
                        <td className="r aa-number">{g.err_spread > 0 ? "+" : ""}{n2(g.err_spread)}</td>
                        <td className="r aa-number">{g.err_total > 0 ? "+" : ""}{n2(g.err_total)}</td>
                        <td className="r"><span className={g.winner_correct ? "aa-good" : "aa-bad"}>{g.winner_correct ? "✓" : "✗"}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
      <BandTuning />
    </section>
  );
}
